import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import sendGaComponentEvent from '@gc-npm/utils.metrics.send-ga-component-event';
import { COLORS } from '@gc-npm/utils.enums.colors';
import { useFormSkeletonSubscribe, usePreviousConditioned } from '@/utils/hooks';

export const COMPONENT_NAME = 'CheckBox';

export const CheckBox = ({
  name,
  value,
  label,
  nodeLabel,
  ariaLabelledBy,
  onChange,
  formSkeletonProps,
  error,
  errorMsg,
  className,
  required,
  taggingInfo,
  checked,
  children,
  hidden,
}) => {
  const {
    submitTries,
    activateFormElement,
    deactivateFormElement,
    validateFormElement,
    validateForm,
  } = { ...formSkeletonProps };
  useFormSkeletonSubscribe(activateFormElement, deactivateFormElement);
  const prevSubmitTries = usePreviousConditioned(submitTries, validateForm);
  const [validationError, setValidationError] = useState('');

  const checkBoxTaggingInfo = {
    componentName: COMPONENT_NAME,
    componentProduct: taggingInfo?.componentProduct || '',
    componentContent: taggingInfo?.componentContent || '',
    componentVariation: taggingInfo?.componentVariation || '',
  }

  useEffect(() => {
    setValidationError(error);
  }, [error]);

  useEffect(() => {
    if (formSkeletonProps) {
      let validationObj = {};
      if (prevSubmitTries !== submitTries && submitTries > 0) {
        if (!checked && required) {
          validationObj = {
            validated: false,
            errorMsg,
          };
        } else {
          validationObj = {
            validated: true,
            errorMsg: '',
          };
        }
        setValidationError(validationObj.errorMsg);
        validateFormElement(validationObj);
      }
    }
  }, [submitTries, prevSubmitTries]);

  const handleOnChange = (e) => {
    const isChecked = e.target.checked;
    sendGaComponentEvent({
      ...checkBoxTaggingInfo,
      componentContent: `${checkBoxTaggingInfo.componentContent}-${isChecked ? 'marcar' : 'desmarcar'}`,
      eventType: 'click',
    });

    if (onChange) {
      onChange(isChecked);
    }

    if (isChecked) {
      setValidationError('');
    }
  };

  const checkboxLabel = label || nodeLabel;

  return (
    <>
      <div className={`CheckBox ${className} ${hidden ? 'CheckBox-hide' : ''}`}>
        <label htmlFor={name} className="label">
          {checkboxLabel && <div className="labelText">{checkboxLabel}</div>}
          <input
            value={value}
            name={name}
            id={name}
            type="checkbox"
            aria-labelledby={ariaLabelledBy}
            checked={checked}
            onChange={handleOnChange}
          />
          <span className={`checkmark ${validationError ? 'error' : ''}`} />
        </label>
        {validationError && (
          <div className="error">{validationError || '\u00A0'}</div>
        )}
      </div>
      {children}
      <style jsx>
        {`
          .CheckBox {
            &-hide {
              display: none;
            }
            .error {
              color: ${COLORS.red};
              font-size: 0.85rem;
              position: absolute;
            }
            .label {
              padding: 0rem 0.75rem !important;
              position: sticky;
              user-select: none;
              .labelText {
                padding-top: 1rem;
              }
              ~ {
                .error {
                  margin-top: 1.5rem;
                }
              }
              input {
                /* Hide the browser's default checkbox */
                position: absolute;
                opacity: 0;
                cursor: pointer;
                height: 0;
                width: 0;
                &:checked {
                  ~ {
                    .checkmark {
                      /* When the checkbox is checked, add a blue background */
                      background-color: ${COLORS.movistarBlue};
                      &:after {
                        display: block;
                      }
                    }
                  }
                }
              }
              .checkmark {
                /* Create a custom checkbox */
                position: absolute;
                top: 0;
                left: 0;
                height: 20px;
                width: 20px;
                border: solid 1px ${COLORS.grey500};
                background-color: ${COLORS.white};
                border-radius: 4px;
                cursor: pointer;
                &:after {
                  content: "";
                  position: absolute;
                  display: none;
                  left: 5px;
                  width: 6px;
                  height: 14px;
                  border: solid ${COLORS.white};
                  border-width: 0 1px 1px 0;
                  transform: rotate(45deg);
                }
                &.error {
                  border-color: ${COLORS.red};
                }
              }
              input:focus-visible ~ .checkmark {
                outline: 2px solid #005eeb;
                outline-offset: 3px;
              }
              &:hover input {
                &~ .checkmark {
                  /* On mouse-over, add a grey background color */
                  background-color: ${COLORS.grey200};
                }
                &:checked ~ .checkmark {
                  background-color: ${COLORS.movistarBlue};
                }
              }
            }
          }
        `}
      </style>
    </>
  );
};

CheckBox.propTypes = {
  /**
   * {
   *  "info": "Nombre que tomará el elemento en el DOM",
   *  "kind": "both",
   *  "beautifulName": "Name"
   * }
   */
  name: PropTypes.string.isRequired,
  /**
   * {
   *  "info": "Función ejecutada al marcar o desmarcar el checkBox ",
   *  "kind": "both",
   *  "beautifulName": "onChange"
   * }
   */
  onChange: PropTypes.func,
  /**
   * {
   *  "info": "Define el contenido que hará las funciones de label",
   *  "kind": "both",
   *  "beautifulName": "Etiqueta nodo"
   * }
   */
  nodeLabel: PropTypes.node,
  /**
   * {
   *  "info": "Texto que aparecerá como label del checkBox",
   *  "kind": "both",
   *  "beautifulName": "Etiqueta"
   * }
   */
  label: PropTypes.string,
  ariaLabelledBy: PropTypes.string,
  /**
   * {
   *  "info": "Define la clase CSS custom que se aplicará al componente",
   *  "kind": "both",
   *  "beautifulName": "ClassName"
   * }
   */
  className: PropTypes.string,
  /**
   * {
   *  "info": "Valor enviado del checkBox",
   *  "kind": "both",
   *  "beautifulName": "Valor"
   * }
   */
  value: PropTypes.string,
  /**
   * {
   *  "info": "Marca o desmarca por defecto del checkBox",
   *  "kind": "both",
   *  "beautifulName": "ValorPorDefecto"
   * }
   */
  defaultValue: PropTypes.bool,
  /**
   * {
   *  "info": "Permite contribuir la infomación que será enviada a analítica tras la interacción (objeto para el taggeado)",
   *  "kind": "both",
   *  "beautifulName": "Propiedades tagueo"
   * }
   */
  taggingInfo: PropTypes.shape({
    event: PropTypes.string,
    eventType: PropTypes.string,
    componentName: PropTypes.string,
    componentVariation: PropTypes.string.isRequired,
    componentContent: PropTypes.string.isRequired,
    componentProduct: PropTypes.string.isRequired,
    componentPerso: PropTypes.any,
    debug: PropTypes.bool,
    customizerId: PropTypes.string,
  }),
  /**
   * {
   *  "info": "Propiedades de formSkeleton necesarias para su integración con este componente",
   *  "kind": "both",
   *  "beautifulName": "formSkeletonProps"
   * }
   */
  formSkeletonProps: PropTypes.shape({
    submitTries: PropTypes.number.isRequired,
    validationTries: PropTypes.number.isRequired,
    activateFormElement: PropTypes.func.isRequired,
    deactivateFormElement: PropTypes.func.isRequired,
    validateFormElement: PropTypes.func.isRequired,
    validateForm: PropTypes.bool.isRequired,
  }),
  /**
   * {
   *  "info": "Define si es obligatorio marcar el checkBox o no",
   *  "kind": "both",
   *  "beautifulName": "Requerido"
   * }
   */
  required: PropTypes.bool,
  /**
   * {
   *  "info": "Define si es visible o no",
   *  "kind": "both",
   *  "beautifulName": "hidden"
   * }
   */
  hidden: PropTypes.bool,
  /**
   * {
   *  "info": "Error a mostrar forzado desde el padre",
   *  "kind": "both",
   *  "beautifulName": "Error"
   * }
   */
  error: PropTypes.string,
  errorMsg: PropTypes.string,
};

CheckBox.defaultProps = {
  value: '',
  label: '',
  defaultValue: false,
  taggingInfo: {},
  formSkeletonProps: null,
  error: '',
  errorMsg: 'Por favor, rellena este campo',
  className: '',
  required: false,
  hidden: false,
};

export default CheckBox;
